
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import { useNavigate } from 'react-router-dom';

export default function WhatWeDo() {
  const { ref, isVisible } = useScrollAnimation();
  const navigate = useNavigate();

  const handleContactClick = () => {
    navigate('/');
    setTimeout(() => {
      const el = document.getElementById('contact');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 300);
  };

  const services = [
    {
      id: 'private',
      label: 'PRIVATE SPACE',
      titleJa: '個人の仕事・創作・生活に寄り添うワーク＆ライフスタイルプロダクトを企画・開発・展開します。',
      titleEn: 'We create products and brands that belong to personal work, creative life, and lifestyle.',
      items: [
        'MeVER products',
        'Chairs',
        'Accessories'
      ],
      image: 'https://public.readdy.ai/ai/img_res/16e25dd74f4b5deacec40594444adba8.jpg',
      link: '/mever',
      ctaLabel: 'MeVER 製品を見る',
      ctaLabelEn: 'View MeVER Products',
      ctaIcon: 'ri-arrow-right-line'
    },
    {
      id: 'public',
      label: 'PUBLIC SPACE',
      titleJa: '海外クリエイティブスタジオと協業し、インタラクティブ体験プロジェクトの日本展開・実装を行います。',
      titleEn: 'We deliver interactive installations and experiential projects in collaboration with global studios.',
      items: [
        'Interactive installations',
        'Experiential projects',
        'Global studio partnerships'
      ],
      image: 'https://readdy.ai/api/search-image?query=Interactive%20art%20installation%20in%20modern%20gallery%20space%20with%20ambient%20lighting%2C%20people%20experiencing%20immersive%20digital%20projection%2C%20contemporary%20museum%20interior%20with%20clean%20white%20walls%2C%20sophisticated%20technology%20integration%2C%20atmospheric%20lighting%20creating%20engaging%20environment%2C%20professional%20architectural%20photography%20with%20subtle%20color%20grading&width=800&height=600&seq=bitmoves-public-001&orientation=landscape',
      link: '/mirari-echo',
      ctaLabel: 'Echo プロジェクトを見る',
      ctaLabelEn: 'View Echo Project',
      ctaIcon: 'ri-arrow-right-line'
    },
    {
      id: 'military',
      label: 'US MILITARY',
      titleJa: '在日米軍基地など、グローバル基準空間における調達・施工・実装支援を提供します。',
      titleEn: 'We support procurement, build, and delivery within global-standard environments including U.S. military bases.',
      items: [
        'FF&E / interiors / small construction',
        'Procurement & delivery support',
        'Base-ready operations'
      ],
      image: 'https://readdy.ai/api/search-image?query=Professional%20modern%20office%20interior%20construction%20site%20with%20clean%20organized%20workspace%2C%20neutral%20color%20palette%20showing%20furniture%20installation%20and%20interior%20fit-out%2C%20simple%20background%20highlighting%20professional%20service%20delivery%2C%20architectural%20photography%20with%20natural%20lighting%20sophisticated%20commercial%20space%20setup%2C%20minimal%20and%20clean%20aesthetic&width=800&height=600&seq=bitmoves-military-001&orientation=landscape',
      link: null,
      ctaLabel: 'お問い合わせ',
      ctaLabelEn: 'Contact Us',
      ctaIcon: 'ri-mail-send-line'
    }
  ];

  return (
    <section ref={ref} id="what-we-do" className="bg-gray-50 py-36 lg:py-44 px-6 lg:px-32">
      <div className="max-w-7xl mx-auto">
        <div className={`mb-20 flex flex-col sm:flex-row items-baseline gap-4 sm:gap-6 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-medium text-gray-900">What We Do</h2>
          <span className="text-base text-gray-500">事業内容</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">
          {services.map((service, index) => {
            const isContactCard = service.link === null;

            const cardContent = (
              <>
                <div className="w-full h-48 sm:h-56 overflow-hidden relative">
                  <img
                    src={service.image}
                    alt={service.label}
                    className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-500 flex items-center justify-center">
                    <span className="opacity-0 group-hover:opacity-100 transition-all duration-500 translate-y-3 group-hover:translate-y-0 bg-white text-gray-900 text-sm font-medium px-6 py-2.5 rounded-full shadow-lg whitespace-nowrap">
                      {service.ctaLabel}
                      <i className={`${service.ctaIcon} ml-2 text-sm`}></i>
                    </span>
                  </div>
                </div>

                <div className="p-8 sm:p-12">
                  <p className="text-xs uppercase tracking-wider text-gray-400 mb-5">
                    {service.label}
                  </p>

                  <div className="mb-8">
                    <p className="text-lg sm:text-xl font-medium leading-relaxed text-gray-900 mb-3">
                      {service.titleJa}
                    </p>
                    <p className="text-sm leading-relaxed text-gray-600">
                      {service.titleEn}
                    </p>
                  </div>

                  <ul className="space-y-3">
                    {service.items.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3 text-sm text-gray-600">
                        <span className="text-gray-300 mt-1">—</span>
                        <span className="leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-8 pt-6 border-t border-gray-100">
                    <span className="text-sm font-medium text-gray-900 flex items-center gap-2 group-hover:gap-3 transition-all duration-300">
                      {service.ctaLabel} / {service.ctaLabelEn}
                      <i className={`${service.ctaIcon} text-base transition-transform duration-300 group-hover:translate-x-1`}></i>
                    </span>
                  </div>
                </div>
              </>
            );

            const className = `group bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-500 hover:-translate-y-1 overflow-hidden ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`;

            const style = { transitionDelay: isVisible ? `${index * 150 + 200}ms` : '0ms' };

            if (service.link) {
              return (
                <Link
                  key={service.id}
                  to={service.link}
                  className={`${className} cursor-pointer block`}
                  style={style}
                >
                  {cardContent}
                </Link>
              );
            }

            return (
              <div
                key={service.id}
                className={`${className} cursor-pointer`}
                style={style}
                onClick={handleContactClick}
              >
                {cardContent}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
